import React, { useEffect, useRef, useState } from 'react';
import { FileText, Minus, Plus, Trash2, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePreinvoice } from '../context/PreinvoiceContext';
import { mediaUrl } from '../services/api';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import CartSyncStatus from './CartSyncStatus';

export default function FloatingPreinvoiceCart() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { items, setQty, removeItem, lastAdded } = usePreinvoice();
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState(false);
  const hasOpenedForFirstItem = useRef(false);
  const isPreinvoicePage = pathname.startsWith('/preinvoice');

  useEffect(() => {
    if (!lastAdded || isPreinvoicePage || hasOpenedForFirstItem.current) return undefined;
    hasOpenedForFirstItem.current = true;
    setOpen(true);
    setNotice(true);
    const t = setTimeout(() => setNotice(false), 5200);
    return () => clearTimeout(t);
  }, [lastAdded, isPreinvoicePage]);

  useEffect(() => {
    const openCart = () => {
      if (window.location.pathname.startsWith('/preinvoice')) return;
      setOpen(true);
      setNotice(false);
    };
    window.addEventListener('open-preinvoice-cart', openCart);
    return () => window.removeEventListener('open-preinvoice-cart', openCart);
  }, []);

  useEffect(() => {
    if (!isPreinvoicePage) return;
    setOpen(false);
    setNotice(false);
  }, [isPreinvoicePage]);

  useBodyScrollLock(open && !isPreinvoicePage);

  if (isPreinvoicePage) return null;

  const goToPreinvoice = () => {
    setOpen(false);
    navigate('/preinvoice');
  };

  return (
    <>
      {notice && (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setNotice(false);
          }}
          className="fixed left-4 top-[300px] z-[70] w-64 rounded-2xl border border-blue-100 bg-white p-3 text-right text-xs font-semibold leading-6 text-slate-700 shadow-2xl"
          dir="rtl"
        >
          محصول اضافه‌شده را مشاهده کنید.
        </button>
      )}

      {open && <div className="fixed inset-0 z-[74] bg-slate-950/35 backdrop-blur-[3px]" onClick={() => setOpen(false)} />}

      <aside
        className={[
          'fixed left-0 top-0 z-[75] h-screen w-[376px] max-w-[90vw] overscroll-contain border-r border-gray-200 bg-white shadow-2xl transition-[transform,opacity] duration-500 ease-out',
          open ? 'translate-x-0 opacity-100' : 'pointer-events-none -translate-x-full opacity-0',
        ].join(' ')}
        dir="rtl"
        aria-hidden={!open}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4">
            <div>
              <div className="text-sm font-extrabold text-slate-900">سبد پیش‌فاکتور</div>
              <div className="mt-1 text-xs text-slate-500">{items.length.toLocaleString('fa-IR')} آیتم</div>
              <CartSyncStatus />
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              aria-label="بستن"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-auto overscroll-contain p-4">
            {items.length ? (
              <div className="space-y-3">
                {items.map((it) => {
                  const key = it.cart_id || it.id;
                  const quantityValue = it.quantity === '' ? '' : Number(it.quantity) || 1;
                  const quantityNumber = Number(it.quantity) || 1;
                  const image = mediaUrl(it.image_url || it.image || it.thumbnail_url || it.thumbnail || it.category_image_url);
                  const code = it.product_code || it.sku;

                  return (
                    <div key={key} className="rounded-2xl border border-gray-200 p-3">
                      <div className="grid grid-cols-[1fr_auto] items-start gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-bold leading-6 text-slate-900">{it.title}</div>
                          <div className="mt-2 flex max-w-full flex-wrap items-center gap-2 text-[11px] font-semibold text-slate-500">
                            {it.brand_name ? (
                              <span className="inline-flex rounded-full border border-gray-200 bg-slate-50 px-3 py-1.5">
                                برند | {it.brand_name}
                              </span>
                            ) : null}
                            {code ? (
                              <span className="inline-flex rounded-full border border-gray-200 bg-slate-50 px-3 py-1.5">
                                کد کالا | {code}
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-3 flex items-center gap-2">
                            <div className="flex items-center rounded-full border border-gray-200 bg-white">
                              <button
                                type="button"
                                className="m-1 flex h-8 w-8 items-center justify-center rounded-full text-[#005bbb] transition hover:bg-green-50 hover:text-green-600"
                                onClick={() => setQty(key, quantityNumber + 1)}
                                aria-label="افزایش"
                              >
                                <Plus className="h-4 w-4" />
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={quantityValue}
                                onChange={(e) => setQty(key, e.target.value)}
                                onBlur={(e) => setQty(key, e.target.value || 1)}
                                className="w-10 border-0 bg-transparent text-center text-sm font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                              />
                              <button
                                type="button"
                                className={`m-1 flex h-8 w-8 items-center justify-center rounded-full transition ${quantityNumber <= 1 ? 'cursor-not-allowed text-gray-300' : 'text-[#005bbb] hover:bg-red-50 hover:text-red-600'}`}
                                disabled={quantityNumber <= 1}
                                onClick={() => setQty(key, Math.max(1, quantityNumber - 1))}
                                aria-label="کاهش"
                              >
                                <Minus className="h-4 w-4" />
                              </button>
                            </div>
                            <button
                              type="button"
                              className="flex h-10 w-10 items-center justify-center rounded-full text-red-500 hover:bg-red-50"
                              onClick={() => removeItem(key)}
                              aria-label="حذف"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        {image ? (
                          <img src={image} alt="" className="h-20 w-20 shrink-0 rounded-2xl border border-gray-100 object-cover" loading="lazy" />
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-500">
                هنوز آیتمی اضافه نشده است.
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 bg-white p-4">
            <button
              type="button"
              onClick={goToPreinvoice}
              className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#005bbb] px-4 py-3 text-sm font-extrabold text-white hover:bg-[#004a99]"
            >
              <FileText className="h-5 w-5" />
              مشاهده پیش فاکتور
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
